<script lang="ts">
  import { WebsiteName } from "../../../config"
</script>

<svelte:head>
  <title>Support - {WebsiteName}</title>
  <meta
    name="description"
    content="Get help and support for your Ace The Round account. Find answers to common questions and learn how to make the most of our platform."
  />
</svelte:head>

<div class="container mx-auto px-4 py-8">
  <div class="prose max-w-none">
    <h1>Support Center</h1>
    <p class="text-muted-foreground">
      Find answers to common questions and get the help you need.
    </p>

    <div class="grid md:grid-cols-2 gap-8 mt-8">
      <div>
        <h2>Quick Help</h2>
        <div class="space-y-6">
          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Getting Started</h3>
              <ul class="space-y-2">
                <li>
                  <a href="/#how-it-works" class="link link-primary"
                    >How It Works</a
                  >
                </li>
                <li>
                  <a href="/pricing" class="link link-primary">Pricing Plans</a>
                </li>
                <li>
                  <a href="/#faq" class="link link-primary"
                    >Frequently Asked Questions</a
                  >
                </li>
              </ul>
            </div>
          </div>

          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Account & Billing</h3>
              <ul class="space-y-2">
                <li>
                  <a href="/login" class="link link-primary">Account Access</a>
                </li>
                <li>
                  <a href="/pricing" class="link link-primary"
                    >Subscription Management</a
                  >
                </li>
                <li>
                  <a href="/contact_us" class="link link-primary"
                    >Billing Support</a
                  >
                </li>
              </ul>
            </div>
          </div>

          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Technical Support</h3>
              <ul class="space-y-2">
                <li>
                  <a href="/contact_us" class="link link-primary"
                    >Report an Issue</a
                  >
                </li>
                <li>
                  <a href="/#faq" class="link link-primary">Technical FAQs</a>
                </li>
                <li>
                  <a href="/terms" class="link link-primary">Terms of Service</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div>
        <h2>Contact Support</h2>
        <div class="space-y-6">
          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Support Hours</h3>
              <p>Monday - Friday: 9:00 AM - 5:00 PM EST</p>
              <p>Response Time: Within 24 business hours</p>
              <div class="card-actions justify-end mt-4">
                <a href="/contact_us" class="btn btn-primary">Contact Us</a>
              </div>
            </div>
          </div>

          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Resources</h3>
              <ul class="space-y-2">
                <li>
                  <a href="/blog" class="link link-primary">Blog & Updates</a>
                </li>
                <li>
                  <a href="/privacy" class="link link-primary">Privacy Policy</a
                  >
                </li>
                <li>
                  <a href="/cookies" class="link link-primary">Cookie Policy</a>
                </li>
              </ul>
            </div>
          </div>

          <div class="card bg-base-100 shadow-lg">
            <div class="card-body">
              <h3 class="card-title">Connect With Us</h3>
              <div class="flex space-x-4 mt-2">
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-circle btn-outline"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"
                    />
                  </svg>
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn btn-circle btn-outline"
                  aria-label="Visit our Twitter page"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
